import { ScrollVertical } from 'elements/functional/ScrollVertical';
import { ActionButton } from 'elements/minute/sidebar/sidebarComponents/atomic/ActionButton';
import { EHovers } from 'libs/styles/styling';
import { ReactElement, useEffect, useState } from 'react';
import { Localization } from 'static/localization/localization';
import styled, { css } from 'styled-components/macro';

interface Props {
    className?: string;
    scrollRef?: React.RefObject<HTMLDivElement>;
    columns: number;
    items: ReactElement<{ key: string }>[];
    selectedIndex: number;
    initialRowsVisible?: number;
    extraRowsVisibleOnMore?: number;
    maxHeight?: number;
    onClick?: (index: number) => void;
}

const DEFAULT_VISIBLE_ROWS = 4;

export const SidebarGrid = (props: Props): JSX.Element => {
    const { columns, items: allItems, selectedIndex } = props;

    const [visibleRows, setVisibleRows] = useState(
        props.initialRowsVisible ?? DEFAULT_VISIBLE_ROWS
    );

    const singleRow = allItems.length <= columns;

    const visibleItems = allItems.slice(0, columns * visibleRows);
    const hasMoreItems = allItems.length > visibleItems.length;

    const gridEmptySpaces = columns - (visibleItems.length % columns);

    function scroll() {
        if (props.scrollRef.current) {
            props.scrollRef.current.scrollTop = 99999999 + props.scrollRef.current.scrollHeight;
        }
    }

    useEffect(() => {
        if (visibleRows && props.scrollRef) {
            scroll();
        }
    }, [visibleRows]);

    return (
        <S_SidebarGrid className={props.className}>
            <S_ScrollVertical ref={props.scrollRef} light $maxHeight={props.maxHeight ?? 350}>
                <S_Items>
                    {singleRow && (
                        <S_Row>
                            {visibleItems.map((item, i) => (
                                <S_Option
                                    key={item.key}
                                    $selected={selectedIndex === i}
                                    $columns={columns}
                                    onClick={() => {
                                        props.onClick?.(i);
                                    }}
                                >
                                    {item}
                                </S_Option>
                            ))}
                        </S_Row>
                    )}

                    {!singleRow && (
                        <S_Grid $columns={columns}>
                            {visibleItems.map((item, i) => (
                                <S_Option
                                    key={item.key}
                                    $selected={selectedIndex === i}
                                    $columns={columns}
                                    onClick={() => {
                                        props.onClick?.(i);
                                    }}
                                >
                                    {item}
                                </S_Option>
                            ))}
                            {new Array(gridEmptySpaces)
                                .fill(0)
                                .map((_, i) => i)
                                .map((i) => (
                                    <S_EmptySpace key={i} />
                                ))}
                        </S_Grid>
                    )}
                </S_Items>
            </S_ScrollVertical>
            {hasMoreItems && (
                <S_ButtonShowMoreContainer>
                    <ActionButton
                        icon={{ iconName: 'plus' }}
                        text={Localization.MORE}
                        onClick={() => {
                            setVisibleRows((curr) => curr + (props.extraRowsVisibleOnMore ?? 2));
                        }}
                    />
                </S_ButtonShowMoreContainer>
            )}
        </S_SidebarGrid>
    );
};

const S_SidebarGrid = styled.div`
    display: flex;
    justify-content: center;
    flex-direction: column;
    user-select: none;
`;

const S_ScrollVertical = styled(ScrollVertical)<{ $maxHeight: number }>`
    overflow-y: auto;
    max-height: ${(props) => props.$maxHeight}px;
    margin-bottom: 15px;
`;

const S_Items = styled.div`
    display: flex;
    justify-content: center;
`;

const S_ButtonShowMoreContainer = styled.div`
    display: flex;
    justify-content: center;
`;

const S_Option = styled.div<{ $selected: boolean; $columns: number }>`
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;
    border-right: 1px solid #dadada;
    border-top: 1px solid #dadada;
    ${(props) => {
        if (props.$selected) {
            return css`
                background: #ffffff;
                box-shadow: none;
                box-shadow: 0px -5px 2px -4px #ff9d25 inset;
            `;
        } else {
            return css`
                background: #f5f5f5;
                box-shadow: 0px 0px 20px 0px #dedede inset;
            `;
        }
    }}
    &:nth-child(${(props) => props.$columns}n) {
        border-right: none;
    }
    &:nth-child(-n + ${(props) => props.$columns}) {
        border-top: none;
    }
    ${(props) => {
        if (!props.$selected) {
            return css`
                &:hover {
                    box-shadow: ${EHovers.LIGHT4};
                }
            `;
        }
    }}
`;

const S_Grid = styled.div<{ $columns: number }>`
    display: grid;
    grid-template-columns: ${(props) =>
        new Array(props.$columns)
            .fill(0)
            .map(() => 'auto')
            .join(' ')};
    border: 1px solid #dadada;
    background: #f5f5f5;
    border-radius: 7px;
    overflow: hidden;
`;

const S_Row = styled.div`
    border: 1px solid #dadada;
    display: flex;
    border-radius: 7px;
    overflow: hidden;
    ${S_Option}:last-child {
        border-right: none;
    }
`;

const S_EmptySpace = styled.div`
    border-top: 1px solid #dadada;
`;
